import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  Text,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { FaLeaf } from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionDto,
  AwardCriterionType,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
  WeightType,
} from "../../../../../autogen/bff-api";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import {
  AWARD_CRITERION_TYPE_OPTIONS,
  criterionTypeSelectValue,
  deriveEnvironmentType,
  ENVIRONMENT_GROUP_VALUE,
  isEnvironmentCriterionType,
  MIN_ENVIRONMENT_WEIGHTING_PERCENT,
} from "../utils/publicProcurementOptions";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";

// t('Type') t('Name') t('Weight') t('Description') t('Delete criterion')

interface Props {
  eventId: string;
  index: number;
  criterion: AwardCriterionDto;
  isLocked: boolean;
  onChange: (next: AwardCriterionDto) => void;
  onDelete: (index: number) => void;
  // Present only for environment criterion cards: chooses the FOA § 7-9 "no environmental
  // weighting" exemption (the parent owns the cross-criteria mutation + confirmation).
  onChooseExemption?: () => void;
}

export const AwardCriterionCard = ({
  eventId,
  index,
  criterion,
  isLocked,
  onChange,
  onDelete,
  onChooseExemption,
}: Props) => {
  const { t } = useTranslation();
  const formatFieldError = useFieldErrorTranslation();
  const { fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const weightFieldError = fieldError(
    `eventData.awardCriteria.bestPriceQualityRatio.criteria.${index}.weight.number`,
  );

  // Local string buffer absorbs empty/non-numeric drafts; only well-formed numbers propagate
  // to the coordinator. Range checking lives on the BFF (FieldErrorCode_OutOfRange).
  const [localWeight, setLocalWeight] = useState<string>(
    criterion.weight?.number != null ? String(criterion.weight.number) : ""
  );

  useEffect(() => {
    setLocalWeight(criterion.weight?.number != null ? String(criterion.weight.number) : "");
  }, [criterion.weight?.number]);

  // The parent (AwardCriteriaStep) funnels onChange into the save-coordinator with
  // debounceMs=300, so this component propagates every keystroke and lets the
  // coordinator collapse them — a ref keeps us calling the latest parent callback.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const updateType = (value: string) => {
    if (value === ENVIRONMENT_GROUP_VALUE) {
      // Designate this row as the environment criterion. The weight drives the sub-type; with no
      // weight yet, seed the FOA § 7-9 floor (30 % → QUALITY_ENVIRONMENTAL_CRITERIA).
      const currentWeight = criterion.weight?.number ?? null;
      if (currentWeight == null) {
        setLocalWeight(String(MIN_ENVIRONMENT_WEIGHTING_PERCENT));
        onChangeRef.current({
          ...criterion,
          type: AwardCriterionType.QualityEnvironmentalCriteria,
          weight: { number: MIN_ENVIRONMENT_WEIGHTING_PERCENT, type: WeightType.Percentage },
        });
      } else {
        onChangeRef.current({ ...criterion, type: deriveEnvironmentType(currentWeight) });
      }
      return;
    }
    onChangeRef.current({ ...criterion, type: value as AwardCriterionType });
  };
  const updateName = (name: string) => onChangeRef.current({ ...criterion, name });
  const updateDescription = (description: string) => onChangeRef.current({ ...criterion, description });
  const updateWeight = (raw: string) => {
    setLocalWeight(raw);
    const isEnvironment = isEnvironmentCriterionType(criterion.type);
    if (raw === "") {
      // Cleared weight = a weightless environment criterion (still vei B, the specification path).
      // The no-environmental-weighting exemption is never reached this way — it is button-only.
      onChangeRef.current({
        ...criterion,
        weight: undefined,
        ...(isEnvironment ? { type: deriveEnvironmentType(null) } : {}),
      });
      return;
    }
    // Weights are integer-only on this form to side-step locale display,
    // float drift, and BigDecimal precision mismatch with the BFF.
    const parsed = Number.parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(parsed)) return;
    const n = Math.round(parsed);
    onChangeRef.current({
      ...criterion,
      weight: { number: n, type: WeightType.Percentage },
      ...(isEnvironment ? { type: deriveEnvironmentType(n) } : {}),
    });
  };

  return (
    <Box
      data-testid="award-criteria-card"
      borderWidth="1px"
      borderRadius="lg"
      borderColor="smBorder"
      p="4"
      mb="3"
    >
      <HStack align="flex-start" spacing="4" mb="3">
        <FormControl flex="1" isDisabled={isLocked}>
          <FormLabel fontSize="xs" color="smMuted">
            {t("Type")}
          </FormLabel>
          <Select
            size="sm"
            rounded="md"
            value={criterionTypeSelectValue(criterion.type)}
            onChange={(e) => updateType(e.target.value)}
          >
            {AWARD_CRITERION_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
            ))}
          </Select>
        </FormControl>

        <FormControl flex="1" isDisabled={isLocked} isInvalid={!!weightFieldError}>
          <FormLabel fontSize="xs" color="smMuted">
            {t("Weight")}
          </FormLabel>
          <InputGroup size="sm">
            <Input
              type="text"
              inputMode="decimal"
              rounded="md"
              value={localWeight}
              onChange={(e) => updateWeight(e.target.value)}
              onBlur={() =>
                setLocalWeight(
                  criterion.weight?.number != null ? String(criterion.weight.number) : ""
                )
              }
            />
            <InputRightAddon rounded="md">%</InputRightAddon>
          </InputGroup>
          {weightFieldError && (
            <FormErrorMessage fontSize="xs">{formatFieldError(weightFieldError)}</FormErrorMessage>
          )}
        </FormControl>

        <IconButton
          aria-label={t("Delete criterion") ?? "Delete"}
          icon={<DeleteIcon />}
          variant="ghost"
          size="sm"
          isDisabled={isLocked}
          onClick={() => onDelete(index)}
        />
      </HStack>

      <FormControl mb="3" isDisabled={isLocked}>
        <FormLabel fontSize="xs" color="smMuted">
          {t("Name")}
        </FormLabel>
        <Input
          size="sm"
          rounded="md"
          value={criterion.name ?? ""}
          onChange={(e) => updateName(e.target.value)}
        />
      </FormControl>

      <Box>
        <Text fontSize="xs" color="smMuted" mb="1">
          {t("Description")}
          <Text as="span" color="smWarning" ml="1">
            *
          </Text>
        </Text>
        <TipTap
          content={criterion.description ?? ""}
          onUpdate={(value: string) => updateDescription(value)}
          editable={!isLocked}
          toolbar={{ underline: false, headings: false, colors: false, link: false, table: false, image: false, template: false }}
          debounceWait={0}
        />
      </Box>

      {isEnvironmentCriterionType(criterion.type) && onChooseExemption && (
        <Flex justify="flex-end" mt="3">
          <Button
            size="xs"
            variant="outline"
            leftIcon={<FaLeaf />}
            isDisabled={isLocked}
            onClick={onChooseExemption}
          >
            {t("Negligible environmental impact")}
          </Button>
        </Flex>
      )}
    </Box>
  );
};
